import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import Animated, {
  useSharedValue,
  useAnimatedRef,
  FadeInDown,
  LinearTransition,
} from "react-native-reanimated";
import React, { useEffect, useState, useCallback } from "react";
import Bill from "../../../components/stats/Bill";
import i18n from "../../../locales/i18n";
import { Toast } from "react-native-toast-notifications";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import customAxios from "../../../utils/customAxios";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { appActions } from "../../../store/slices/appSlice";
import { useFocusEffect } from "expo-router";
import WebStatsScreen from "../../../components/web/WebStatsScreen";
import InlineCalendarPicker from "../../../components/shared/InlineCalendarPicker";
import {
  StatsData,
  DateOption,
  BillItem,
  fetchStatisticIntervalle,
  getDateRangeForFilter,
} from "../../../utils/statisticApi";

// ─── Main Bills Screen ──────────────────────────────────────────
const BillsScreen = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);

  const [selectedItem, setSelectedItem] = useState<DateOption>({
    label: i18n.t("today"),
    value: "today",
  });

  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  const [initialLoading, setInitialLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [billsLoading, setBillsLoading] = useState(false);

  // Calendar picker state
  const [showCalendar, setShowCalendar] = useState(false);
  const [customStart, setCustomStart] = useState<Date>(new Date());
  const [customEnd, setCustomEnd] = useState<Date>(new Date());

  const flatListRef = useAnimatedRef<FlatList>();
  const selectedIndex = useSharedValue(0);

  const [stats, setStats] = useState<StatsData>({
    totalselles: 0,
    total: 0,
    totalprofit: 0,
    totalcapital: 0,
  });

  const [dateOptions] = useState<DateOption[]>([
    { label: i18n.t("tout"), value: "all" },
    { label: i18n.t("today"), value: "today" },
    { label: i18n.t("yesterday"), value: "yesterday" },
    { label: i18n.t("mounth"), value: "month" },
    { label: i18n.t("year"), value: "year" },
    { label: i18n.t("custom"), value: "custom" },
  ]);

  const [bills, setBills] = useState<BillItem[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Fetch stats using /statistic_intervalle (always, no /statistic fallback)
  const fetchStats = useCallback(
    async (dayValue: string = "all", isInitial: boolean = false) => {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setStatsLoading(true);
        setBillsLoading(true);
      }

      const minLoadingTime = 300;
      const startTime = Date.now();

      try {
        const dates = getDateRangeForFilter(dayValue, customStart, customEnd);
        const data = await fetchStatisticIntervalle(dates.start, dates.end);

        setStats({
          totalselles: data.totalselles || 0,
          total: data.total || 0,
          totalprofit: data.totalprofit || 0,
          totalcapital: data.totalcapital || 0,
        });
        setBills(Array.isArray(data.factures) ? data.factures : []);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setStats({
            totalselles: 0,
            total: 0,
            totalprofit: 0,
            totalcapital: 0,
          });
          setBills([]);
        } else {
          Toast.show(i18n.t("findBillsError"), { type: "danger" });
        }
      } finally {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        setTimeout(() => {
          setInitialLoading(false);
          setStatsLoading(false);
          setBillsLoading(false);
        }, remainingTime);
      }
    },
    [customStart, customEnd],
  );

  const handleChangeDate = useCallback(
    async (day: DateOption) => {
      if (statsLoading || billsLoading) return;

      setSelectedItem(day);
      const index = dateOptions.findIndex((item) => item.value === day.value);
      selectedIndex.value = index;
      flatListRef.current?.scrollToOffset({
        offset: index * 100 - 150,
        animated: true,
      });

      if (day.value === "custom") {
        setShowCalendar(true);
      } else {
        await fetchStats(day.value, false);
      }
    },
    [
      dateOptions,
      selectedIndex,
      flatListRef,
      fetchStats,
      statsLoading,
      billsLoading,
    ],
  );

  const handleCalendarApply = useCallback(
    (start: Date, end: Date) => {
      setCustomStart(start);
      setCustomEnd(end);
      setShowCalendar(false);
      setTimeout(() => {
        fetchStats("custom", false);
      }, 100);
    },
    [fetchStats],
  );

  // We need to refetch when custom dates change
  useEffect(() => {
    if (selectedItem.value === "custom" && !showCalendar) {
      fetchStats("custom", false);
    }
  }, [customStart, customEnd]);

  const handleDelete = useCallback(
    async (id: number) => {
      setDeletingId(id);
      try {
        await customAxios.delete(`/statistic/${id}`, { withCredentials: true });
        setBills((prev) => prev.filter((bill) => bill.id_facture !== id));
        await fetchStats(selectedItem.value, false);
        Toast.show(i18n.t("deleteSuccess"), { type: "success" });
      } catch (error) {
        Toast.show(i18n.t("deleteError"), { type: "danger" });
      } finally {
        setDeletingId(null);
      }
    },
    [selectedItem.value, fetchStats],
  );

  useEffect(() => {
    const index = dateOptions.findIndex(
      (item) => item.value === selectedItem.value,
    );
    selectedIndex.value = index;
    flatListRef.current?.scrollToOffset({
      offset: index * 100 - 150,
      animated: false,
    });
  }, []);

  // Initial data fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchStats("today", true);
    }, 100);
    return () => clearTimeout(timer);
  }, [fetchStats]);

  if (Platform.OS === "web") {
    return <WebStatsScreen />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      <StandardNavigation
        title={i18n.t("bills")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
      />

      {/* Calendar Picker Modal */}
      <InlineCalendarPicker
        visible={showCalendar}
        onClose={() => setShowCalendar(false)}
        onApply={handleCalendarApply}
      />

      <ScrollView showsVerticalScrollIndicator={false}>
        <View className="flex-1">
          {/* Date Selection */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(100)}
            className="sticky z-30 py-2 px-8 my-2 top-0"
          >
            <View className="shadow bg-white rounded-full">
              <Animated.FlatList
                data={dateOptions}
                ref={flatListRef}
                getItemLayout={(_, index) => ({
                  length: 100,
                  offset: 100 * index,
                  index,
                })}
                keyExtractor={(item) => item.value}
                horizontal={true}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    key={item.value}
                    onPress={() => handleChangeDate(item)}
                    style={{
                      borderRadius: 9999,
                      opacity: statsLoading ? 0.6 : 1,
                    }}
                    disabled={statsLoading}
                  >
                    {selectedItem.value === item.value ? (
                      <LinearGradient
                        colors={[
                          "rgba(19, 121, 162, 1)",
                          "rgba(5, 150, 208, 1)",
                        ]}
                        style={{
                          marginHorizontal: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 4,
                          borderRadius: 9999,
                        }}
                      >
                        <Text className="text-white mx-2 text-2xl px-3 py-1 rounded-full">
                          {item.label}
                        </Text>
                      </LinearGradient>
                    ) : (
                      <View
                        style={{
                          marginHorizontal: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 4,
                          borderRadius: 9999,
                          backgroundColor: "white",
                        }}
                      >
                        <Text className="text-gray-700 mx-2 text-2xl px-3 py-1 rounded-full">
                          {item.label}
                        </Text>
                      </View>
                    )}
                  </TouchableOpacity>
                )}
              />
            </View>
          </Animated.View>

          {/* Custom date range display */}
          {selectedItem.value === "custom" && !statsLoading && (
            <Animated.View
              entering={FadeInDown.duration(400)}
              className="px-8 pb-2"
            >
              <TouchableOpacity
                onPress={() => setShowCalendar(true)}
                className="flex-row items-center justify-center gap-2 bg-white rounded-xl py-3 px-4 shadow-sm"
              >
                <MaterialIcons name="date-range" size={20} color="#1379A2" />
                <Text className="text-primaryV2 font-semibold text-base">
                  {customStart.toLocaleDateString()} →{" "}
                  {customEnd.toLocaleDateString()}
                </Text>
                <MaterialIcons name="edit" size={16} color="#1379A2" />
              </TouchableOpacity>
            </Animated.View>
          )}

          {/* Summary Stats Row */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(150)}
            className="px-4 py-2"
          >
            <View className="flex-row gap-3">
              <View className="flex-1 bg-white rounded-xl p-3 shadow-sm items-center">
                <Text className="text-xs text-gray-500 mb-1">
                  {i18n.t("total")}
                </Text>
                {statsLoading ? (
                  <ActivityIndicator size="small" color="#1379A2" />
                ) : (
                  <Text className="text-lg font-bold text-primaryV2">
                    {stats.total} {i18n.t("currency")}
                  </Text>
                )}
              </View>
              <View className="flex-1 bg-white rounded-xl p-3 shadow-sm items-center">
                <Text className="text-xs text-gray-500 mb-1">
                  {i18n.t("nbrofsell")}
                </Text>
                {statsLoading ? (
                  <ActivityIndicator size="small" color="#1379A2" />
                ) : (
                  <Text className="text-lg font-bold text-green-600">
                    {stats.totalselles}
                  </Text>
                )}
              </View>
              <View className="flex-1 bg-white rounded-xl p-3 shadow-sm items-center">
                <Text className="text-xs text-gray-500 mb-1">
                  {i18n.t("profit")}
                </Text>
                {statsLoading ? (
                  <ActivityIndicator size="small" color="#1379A2" />
                ) : (
                  <Text className="text-lg font-bold text-emerald-600">
                    {stats.totalprofit} {i18n.t("currency")}
                  </Text>
                )}
              </View>
            </View>
          </Animated.View>

          {/* Bills Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(200)}
            className="px-4 pb-4"
          >
            <View
              className="p-4 mb-4 flex rounded-2xl bg-white"
              style={{
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 3,
              }}
            >
              <Text
                className={`${
                  locale === "fr" ? "text-left" : "text-right"
                } text-3xl mb-2 text-primaryV2 font-bold`}
              >
                {i18n.t("bills")}
              </Text>

              {billsLoading && (
                <View className="flex justify-center items-center py-8">
                  <ActivityIndicator size="large" color="#279F9E" />
                  <Text className="mt-2 text-gray-600 text-center">
                    {i18n.t("loading")}
                  </Text>
                </View>
              )}

              {!billsLoading && bills.length > 0 && (
                <>
                  {bills.map((item, index) => (
                    <Animated.View
                      key={`animated-bill-${item.id_facture || index}`}
                      entering={FadeInDown.duration(400).delay(
                        100 + index * 50,
                      )}
                      layout={LinearTransition}
                    >
                      <Bill
                        info={item as any}
                        buttonSpinnerDelete={deletingId === item.id_facture}
                        handleDelete={(id) => handleDelete(Number(id))}
                      />
                    </Animated.View>
                  ))}
                </>
              )}

              {!billsLoading && bills.length === 0 && (
                <View className="py-8 flex justify-center items-center">
                  <MaterialIcons name="receipt-long" size={48} color="#ccc" />
                  <Text className="text-center text-lg text-gray-500 mt-2">
                    {i18n.t("nobills")}
                  </Text>
                </View>
              )}
            </View>
          </Animated.View>
        </View>
      </ScrollView>
    </View>
  );
};

export default BillsScreen;
